import { useState } from "react"
import { FilterSchedule, OpenDraft, OpenSchedule } from "event-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { Box, Button, DialogContent, DialogTitle } from "@mui/material"
import Schedules from "routes/schedule/schedules"
import { OnMobile } from "lib/@components/mobile"
import { ListItemBox } from "lib/@components/ListItemBox"
import { SearchSchedules } from "routes/schedule/my-schedules/my-custom-schedules/search-schedules"
import { ScheduleSelectorComponent } from "routes/schedule/components/schedule-selector-component"
import { SCHEDULE } from "library/constants"
import { OkCancel } from "lib/@components/okCancel"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import Iconify from "minimals-template/components/Iconify"
import { useSelectedItems } from "routes/schedule/lib/useSelectedItems"
import { MultiSelectionChipPreviews } from "routes/schedule/my-schedules/my-custom-schedules/multi-selection-chip-previews"
import { MULTI_SELECTION_REFERENCE_TYPE } from "routes/schedule/my-schedules/my-custom-schedules/constants/multi-selection-reference-type"
import { If } from "lib/@components/switch"
import { useSelection } from "routes/schedule/lib/useSelection"
import { IsSelectable } from "routes/schedule/my-schedules/my-custom-schedules/is-selectable.js"

export function SelectMultiSchedules({ title = "Choose schedules", selectionReferenceType = undefined }) {
    const [scrollButtons, setScrollButtons] = useState()
    const [value, setValue] = useState()
    const [previous, setPrevious] = useState(["_root"])
    const [parentId, setParentId] = useState("_root")
    const selectedItems = useSelectedItems(selectionReferenceType)
    const [, selectSchedule] = useSelection(selectionReferenceType)
    const schedulesList =
        selectedItems?.filter(
            (s) => s && (s.type === SCHEDULE || s.$?.type === SCHEDULE || s._id?.includes("schedule"))
        ) ?? []
    const selectedSchedulesCount = schedulesList.length
    const okDisabled = selectedSchedulesCount < 1
    const okCaption = createOkCaption(selectedSchedulesCount)
    const showMultiSelectionChipOptions = selectionReferenceType === MULTI_SELECTION_REFERENCE_TYPE

    OpenSchedule("*").before.useEvent(handleOpenSchedule)
    OpenDraft("*").before.useEvent(() => {
        errorSnackbar("You can't use a draft schedule here")
        return false
    })

    FilterSchedule.after.useEvent(filterRetired)

    return (
        <Bound
            paginationPortal={scrollButtons}
            disableCreation={true}
            inSelector={true}
            isMultiSelect={true}
            transformScheduleItem={wrapInSelectable}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Schedules>
                    <Box overflow="hidden" data-cy="dialog-schedule-selector">
                        <Box display="flex" flexDirection="column" height={1} flex={1} py={2}>
                            <SearchSchedules />
                            <OnMobile>
                                <ListItemBox>
                                    {previous.length > 1 && (
                                        <Box>
                                            <Button
                                                onClick={pop}
                                                startIcon={<Iconify icon="mdi:arrow-left" />}
                                                variant="contained"
                                            >
                                                Back
                                            </Button>
                                        </Box>
                                    )}
                                </ListItemBox>
                            </OnMobile>
                            <ScheduleSelectorComponent key={parentId} id={parentId} onSelect={push} />
                        </Box>
                    </Box>
                </Schedules>
            </DialogContent>
            <ListItemBox pr={4}>
                <Box flex={1} />
                <Box ref={setScrollButtons} />
            </ListItemBox>
            <If truthy={showMultiSelectionChipOptions}>
                <Box sx={{ width: 1, px: 3, pb: 1 }}>
                    <MultiSelectionChipPreviews schedules={schedulesList} onRemove={removeSchedule} />
                </Box>
            </If>
            <OkCancel value={schedulesList} okDisabled={okDisabled} okCaption={okCaption} disableKeys></OkCancel>
        </Bound>
    )

    function filterRetired({ items }) {
        return { items: items.filter((s) => !s.retired) }
    }

    function handleOpenSchedule(schedule) {
        if (schedule?.$?.itemType === "retired") {
            errorSnackbar("You can't use a retired schedule here")
            return false
        }
        if (schedule?.code === "00-01") {
            errorSnackbar("You can't use the Overarching Introduction")
            return false
        }
        if (schedule.type === SCHEDULE || schedule.$?.type === SCHEDULE || schedule._id?.includes("schedule")) {
            if (!schedule.retired) {
                setValue(schedule)
            }
        } else {
            setPrevious((previous) => [...previous, schedule.id])
            setParentId(schedule.id)
        }
        return false
    }

    function wrapInSelectable(children, item) {
        const selectedId = value?.id ?? value?._id
        return (
            <IsSelectable selectedId={selectedId} item={item}>
                {children}
            </IsSelectable>
        )
    }

    function pop() {
        const last = previous.at(-2)
        setPrevious((previous) => previous.slice(0, -1))
        setParentId(last)
    }

    function push(item) {
        setPrevious((previous) => [...previous, item.id])
        setParentId(item.id)
    }

    function removeSchedule(schedule) {
        const id = schedule?._id ?? schedule?.id
        if (!id) return
        selectSchedule(id, false)
    }

    function createOkCaption(count) {
        if (count === 0) {
            return "Select a schedule"
        }
        return `Use ${count} ${"schedule".pluralize(count)}`
    }
}
